var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-456 — hook for the DELETE half of `/czna-authorities/module-settings/{key}`
 * that actually deletes.
 *
 * <b>Why this exists next to `useResetModuleSetting`, which calls the same URL.</b>
 * The endpoint is dual-semantic and dispatches server-side on `user_created`:
 * a packaged row is RESET to its packaged default (200 + body, the row stays),
 * a template NK created is PHYSICALLY DELETED (204, no body). Those are two
 * different user intentions with two different confirmation texts and two
 * different cache consequences — a delete has to drop the row from the template
 * lists, a reset must not. One hook returning "well, one of those happened"
 * would push that discrimination into every caller.
 *
 * The split is by intent, not by guesswork: the caller knows which it wants
 * because `FormConfigTemplateSummary.userCreated` says which kind of row it is
 * looking at. `outcome` is still reported so a caller can notice when the
 * server disagreed with it (e.g. the row was reset because it turned out to be
 * packaged) instead of silently assuming the row is gone.
 *
 * The response is deliberately NOT read with `.json()`: a 204 has no body and
 * parsing one is how you turn a success into a thrown SyntaxError.
 *
 * MEMORY rule: `useOkapiKy()` is captured in the mutation closure, never
 * listed in a hook dep array.
 */
import { useOkapiKy } from '@folio/stripes/core';
import { useMutation, useQueryClient } from 'react-query';
import { CZNA_MODULE_SETTING_DETAIL } from '../types/apiPaths';
import { formConfigTemplatesQueryKey } from './useFormConfigTemplates';
import { moduleSettingInvalidationKeys } from './useModuleSetting';
import { isTimeoutError } from '../utils/errorKind';
var DeleteError = /** @class */ (function (_super) {
    __extends(DeleteError, _super);
    function DeleteError(status, cause) {
        var _this = _super.call(this, "useDeleteModuleSetting failed: HTTP ".concat(status !== null && status !== void 0 ? status : 'unknown')) || this;
        _this.name = 'DeleteError';
        _this.status = status;
        _this.timedOut = isTimeoutError(cause);
        if (cause !== undefined) {
            _this.cause = cause;
        }
        return _this;
    }
    return DeleteError;
}(Error));
var useDeleteModuleSetting = function (key, options) {
    var _a, _b, _c, _d, _e;
    if (options === void 0) { options = {}; }
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var recordType = options.recordType, onSuccess = options.onSuccess;
    var mutation = useMutation(function () { return __awaiter(void 0, void 0, void 0, function () {
        var response, err_1, status_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, ky.delete(CZNA_MODULE_SETTING_DETAIL(key))];
                case 1:
                    response = _b.sent();
                    return [2 /*return*/, response.status === 204 ? 'deleted' : 'reset'];
                case 2:
                    err_1 = _b.sent();
                    status_1 = (_a = err_1 === null || err_1 === void 0 ? void 0 : err_1.response) === null || _a === void 0 ? void 0 : _a.status;
                    throw new DeleteError(status_1, err_1);
                case 3: return [2 /*return*/];
            }
        });
    }); }, {
        onSuccess: function (outcome) {
            if (recordType) {
                queryClient.invalidateQueries(formConfigTemplatesQueryKey(recordType));
            }
            queryClient.invalidateQueries(formConfigTemplatesQueryKey());
            moduleSettingInvalidationKeys(key)
                .forEach(function (queryKey) { return queryClient.invalidateQueries(queryKey); });
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(outcome);
        },
    });
    return {
        mutate: function () { return mutation.mutate(); },
        isLoading: mutation.isLoading,
        isError: mutation.isError,
        errorStatus: (_b = (_a = mutation.error) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : null,
        timedOut: (_d = (_c = mutation.error) === null || _c === void 0 ? void 0 : _c.timedOut) !== null && _d !== void 0 ? _d : false,
        outcome: (_e = mutation.data) !== null && _e !== void 0 ? _e : null,
        reset: mutation.reset,
    };
};
export default useDeleteModuleSetting;
